import React from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import i18n from "../translations";

/**
 * Returns true when a shipping method's real carrier differs from its
 * customer-facing display name — i.e. it's a Cubbo whitelabel (e.g. UBER shown
 * as "Cubbo"). Data-driven: no hardcoded carrier names, so it covers any future
 * whitelabel automatically. (CUBBO-2185)
 */
export const hasHiddenInternalCarrier = (carrierName, internalCarrierName) =>
    !!internalCarrierName && internalCarrierName !== carrierName;

/**
 * The real underlying carrier, for management/operational surfaces where the
 * carrier is handled as its own entity (Lastmile shipping-method & coverage
 * management, dispatch schedules, shipment generation). Uber must NOT be grouped
 * or shown under the Cubbo whitelabel there. Falls back to the display name when
 * no internal carrier is present. (CUBBO-2185)
 */
export const realCarrierName = (carrierName, internalCarrierName) =>
    internalCarrierName || carrierName;

/**
 * Renders a shipping carrier name. Two modes (CUBBO-2185):
 *
 * - Default (order-presentation surfaces): shows the customer-facing
 *   `carrierName` (e.g. "Cubbo"). When the real carrier differs it appends an
 *   info icon whose tooltip reveals the real carrier (e.g. "Uber"), so operators
 *   know what is actually delivering.
 * - `preferInternal` (carrier/method management & dispatch surfaces): shows the
 *   real `internalCarrierName` (e.g. "Uber") so it's handled as its own carrier.
 *   When it differs, the tooltip reveals the client-facing name ("shown to
 *   clients as Cubbo").
 *
 * When the two names match (every non-whitelabel carrier) it renders the plain
 * name — no icon, no duplicated text.
 *
 * @param {string}  carrierName          - shipping_method.carrier_name (display)
 * @param {string}  internalCarrierName  - shipping_method.internal_carrier_name (real)
 * @param {boolean} [preferInternal]     - show the real carrier as primary (management surfaces)
 * @param {string}  [suffix]             - optional trailing text, e.g. shipping_name
 * @param {string}  [separator]          - separator between name and suffix (default " - ")
 * @param {string}  [className]          - extra classes on the wrapper
 * @param {string}  [placeholder]        - text when the primary name is blank (default "-")
 */
export const CarrierName = ({
    carrierName,
    internalCarrierName,
    preferInternal = false,
    suffix,
    separator = " - ",
    className = "",
    placeholder = "-",
}) => {
    const differ = hasHiddenInternalCarrier(carrierName, internalCarrierName);
    const primary = preferInternal ? realCarrierName(carrierName, internalCarrierName) : carrierName;
    const baseName = primary || placeholder;
    const label = suffix ? `${baseName}${separator}${suffix}` : baseName;

    const tooltip = preferInternal
        ? i18n.t("common.client_carrier_tooltip", { carrier: carrierName })
        : i18n.t("common.internal_carrier_tooltip", { carrier: internalCarrierName });

    return (
        <span className={`inline-flex items-center gap-1 ${className}`}>
            <span>{label}</span>
            {differ && (
                <Tooltip title={tooltip}>
                    <InfoCircleOutlined className="text-gray-400 cursor-help" />
                </Tooltip>
            )}
        </span>
    );
};

export default CarrierName;
